// Web onboarding — a NEW business account, online only: phone → code → PIN
// through the Authentik business-enrollment flow (authentik_flow.ts enroll /
// submitOtp / setPin), ending with a session exactly like a sign-in.
//
// One Enrollment per wizard: the flow's executor state lives on its client
// between the screens. The transport is injectable so the mock build (and the
// tests) drive the same client against a scripted fake Authentik.
// Node mode never enrolls here: a node has its own first run (/setup).

import { BUSINESS_AUTH } from '../env';
import { lockStore } from '../idle-lock';
import { bizUid, normalizeIraqiPhone } from '../digits';
import { flowLoginResult } from './authentik';
import { AuthentikFlowClient } from './authentik_flow';
import { httpOtpClient, type OtpChannelInfo, type OtpClient } from './otp_exchange';
import { activeStrategyName } from './index';
import { sessionStore } from './session';
import { AuthError, AUTH_COPY, type LoginResult } from './types';

export interface Enrollment {
  /** E.164 phone of the account being created ('' before start). */
  readonly phone: string;
  /** Identify the phone and send the code. Throws AuthError ('already_registered', 'locked', …). */
  start(phone: string): Promise<void>;
  /** Send the code again (same flow). */
  resend(): Promise<void>;
  /**
   * Check the code; next is the PIN. When the last send went through an
   * exchange channel (Asiacell SMS) the typed code is first exchanged with
   * ihjizli-otp for the Authentik code. Throws AuthError('invalid_code' +
   * attemptsLeft | 'code_expired' | 'locked' | 'network').
   */
  submitCode(code: string): Promise<void>;
  /**
   * Read back which channel delivered the last code (ihjizli-otp
   * GET /v1/auth/otp/channel) and keep it. Never throws; null = unknown.
   * start() and resend() call it, so channelInfo() is ready for the screen.
   */
  channel(): Promise<OtpChannelInfo | null>;
  /** The channel last read back (null until known or when it cannot be named). */
  channelInfo(): OtpChannelInfo | null;
  /** Set the PIN; the account exists and the session is stored. Throws AuthError('weak_pin'). */
  setPin(pin: string): Promise<LoginResult>;
  /** Where the flow stands: waiting for the code, the PIN, or nothing. */
  stage(): 'otp' | 'pin' | null;
  reset(): void;
}

export function createEnrollment(opts: { fetch?: typeof fetch; otp?: OtpClient } = {}): Enrollment {
  const client = new AuthentikFlowClient(BUSINESS_AUTH.clientId, { fetch: opts.fetch });
  // The ihjizli-otp calls go through the global fetch (same-origin /api/otp):
  // under the mock build the interceptor answers them (onboarding/mock/handlers.ts).
  const otp = opts.otp ?? httpOtpClient;
  let phone = '';
  let info: OtpChannelInfo | null = null;
  // The last exchange: a typed code already traded for its Authentik code. The
  // pending record is one-shot server-side, so a retry of the SAME typed code
  // (Authentik unreachable the first time) must reuse the answer, not re-exchange.
  let exchanged: { typed: string; authentik: string } | null = null;
  const readChannel = async (): Promise<OtpChannelInfo | null> => {
    info = phone ? await otp.channel(phone) : null;
    return info;
  };
  return {
    get phone() {
      return phone;
    },
    async start(input) {
      const uid = bizUid(input);
      const e164 = normalizeIraqiPhone(input);
      if (!uid || !e164) throw new AuthError('invalid_phone', AUTH_COPY.invalidPhone);
      phone = e164;
      info = null;
      exchanged = null;
      await client.enroll(uid, e164);
      await readChannel();
    },
    async resend() {
      // A new code replaces the pending record server-side: forget the old answers.
      info = null;
      exchanged = null;
      await client.resendOtp();
      await readChannel();
    },
    async submitCode(code) {
      // The send may not have been on record yet when start() read it back.
      const known = info ?? (await readChannel());
      let toSubmit = code;
      if (known?.exchange) {
        if (exchanged && exchanged.typed === code) {
          toSubmit = exchanged.authentik;
        } else {
          const real = await otp.exchange(phone, code);
          // null = no exchange on record for this send: the typed code is Authentik's.
          if (real) {
            exchanged = { typed: code, authentik: real };
            toSubmit = real;
          }
        }
      }
      await client.submitOtp(toSubmit);
    },
    channel: readChannel,
    channelInfo: () => info,
    async setPin(pin) {
      const tokens = await client.setPin(pin);
      const result = flowLoginResult(tokens, phone);
      // Same as signIn(): the store remembers which strategy renews/unlocks
      // this session (authentik online; local under the mock interceptor,
      // whose fake login endpoints answer the lock screen).
      sessionStore.set(result, activeStrategyName(), { fresh: true });
      lockStore.unlock();
      return result;
    },
    stage: () => client.enrollmentStage(),
    reset() {
      phone = '';
      info = null;
      exchanged = null;
      client.resetEnrollment();
    },
  };
}
